import AddToHomeScreenRoundedIcon from "@mui/icons-material/AddToHomeScreenRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import { APP_UI } from "../ui/appUi";
import AppBottomSheet from "./AppBottomSheet";
import AppCard from "./AppCard";

function isStandalone(): boolean {
  return window.matchMedia?.("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone);
}

type InstallPlatform = "android" | "ios" | "other";

function detectInstallPlatform(): InstallPlatform {
  const ua = navigator.userAgent.toLowerCase();
  const platform = navigator.platform?.toLowerCase() ?? "";
  const touchMac = platform.includes("mac") && navigator.maxTouchPoints > 1;
  if (/android/.test(ua)) return "android";
  if (/iphone|ipad|ipod/.test(ua) || touchMac) return "ios";
  return "other";
}

export default function PwaInstallOnboarding({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [guideOpen, setGuideOpen] = useState(false);
  const platform = useMemo(() => detectInstallPlatform(), []);

  useEffect(() => {
    if (open) return;
    const timer = window.setTimeout(() => setGuideOpen(false), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  if (isStandalone()) return null;

  return (
    <>
      <AppBottomSheet
        open={open}
        onClose={onClose}
        title="Materialentnahme installieren"
        subtitle="Für die beste Nutzung als App auf dem Gerät installieren"
        leading={<AddToHomeScreenRoundedIcon sx={{ color: APP_UI.accent }} />}
        maxHeight="80dvh"
      >
        <Stack spacing={1.1}>
          <Alert severity="info" sx={{ borderRadius: 2.4 }}>
            Als installierte PWA läuft Materialentnahme im eigenen App-Fenster. Besonders auf iPhone/iPad ist die Installation auch für Web-Push-Benachrichtigungen wichtig.
          </Alert>
          <Button
            variant="outlined"
            startIcon={<InfoOutlinedIcon />}
            onClick={() => setGuideOpen(true)}
            sx={{ minHeight: 46, borderRadius: 999, textTransform: "none", fontWeight: 850, color: APP_UI.accent, borderColor: APP_UI.accentBorder }}
          >
            Anleitung anzeigen
          </Button>
          <Typography variant="caption" sx={{ color: APP_UI.textMuted, textAlign: "center" }}>
            Du kannst den Hinweis schließen und Materialentnahme weiterhin im Browser verwenden.
          </Typography>
        </Stack>
      </AppBottomSheet>

      <AppBottomSheet
        open={open && guideOpen}
        onClose={() => setGuideOpen(false)}
        title="App installieren"
        subtitle={platform === "android" ? "Android" : platform === "ios" ? "iPhone / iPad" : "Installationshinweis"}
        leading={<InfoOutlinedIcon sx={{ color: APP_UI.accent }} />}
        maxHeight="88dvh"
        zIndex={1760}
      >
        <Stack spacing={1}>
          {platform === "android" && (
            <GuideCard
              icon={<AndroidIcon sx={{ color: "#7dcc7d" }} />}
              title="Android"
              steps={[
                "Materialentnahme in Chrome öffnen.",
                <span>Das Browser-Menü über <b>⋮</b> öffnen.</span>,
                <span>Je nach Chrome-Version <b>„Installieren und Verknüpfung erstellen“</b>, <b>„App installieren“</b> oder <b>„Zum Startbildschirm hinzufügen“</b> auswählen.</span>,
                "Die Installation bestätigen. Materialentnahme kann anschließend direkt über das neue App-Symbol gestartet werden.",
              ]}
            />
          )}
          {platform === "ios" && (
            <>
              <GuideCard
                icon={<AppleIcon sx={{ color: APP_UI.text }} />}
                title="iPhone / iPad"
                steps={[
                  "Materialentnahme in Safari öffnen.",
                  <span>Das Browser-Menü über <MoreHorizRoundedIcon sx={{ fontSize: 16, verticalAlign: "middle", mx: 0.2 }} /> öffnen.</span>,
                  <span><b>„Teilen“</b> auswählen.</span>,
                  <span>Die zusätzlichen Optionen über <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18, verticalAlign: "middle", mx: 0.15 }} /> einblenden.</span>,
                  <span><b>„Zum Home-Bildschirm“</b> auswählen.</span>,
                  <span>Sicherstellen, dass <b>„Als Web-App öffnen“</b> aktiviert ist.</span>,
                  <span>Mit <b>„Hinzufügen“</b> bestätigen. Materialentnahme kann anschließend direkt über das neue App-Symbol geöffnet werden.</span>,
                ]}
              />
              <Alert severity="info" sx={{ borderRadius: 2.4 }}>
                Auf iPhone/iPad sind Web-Push-Benachrichtigungen für diese PWA erst nach dem Hinzufügen zum Home-Bildschirm sinnvoll nutzbar.
              </Alert>
            </>
          )}
          {platform === "other" && (
            <GuideCard
              icon={<DevicesRoundedIcon sx={{ color: APP_UI.accent }} />}
              title="Dieses Gerät"
              steps={[
                "Für dieses Gerät ist derzeit keine passende mobile Installationsanleitung hinterlegt.",
                "Falls dein Browser PWAs unterstützt, findest du die Installationsfunktion normalerweise im Browser-Menü oder in der Adressleiste.",
              ]}
            />
          )}
          <Button onClick={() => setGuideOpen(false)} sx={{ color: APP_UI.accent, textTransform: "none", fontWeight: 850 }}>Zurück</Button>
        </Stack>
      </AppBottomSheet>
    </>
  );
}

function GuideCard({ icon, title, steps }: { icon: ReactNode; title: string; steps: ReactNode[] }) {
  return (
    <AppCard sx={{ p: 1.15 }}>
      <Stack direction="row" spacing={0.8} sx={{ alignItems: "center", mb: 0.7 }}>
        {icon}
        <Typography sx={{ color: APP_UI.text, fontWeight: 950 }}>{title}</Typography>
      </Stack>
      <Stack spacing={0.55}>
        {steps.map((step, index) => (
          <Stack key={index} direction="row" spacing={0.8} sx={{ alignItems: "flex-start" }}>
            <Typography variant="caption" sx={{ color: APP_UI.accent, fontWeight: 950, minWidth: 18 }}>{index + 1}.</Typography>
            <Typography variant="caption" sx={{ color: APP_UI.textMuted, lineHeight: 1.5 }}>{step}</Typography>
          </Stack>
        ))}
      </Stack>
    </AppCard>
  );
}
